import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { Search, Plus, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

function NewCustomerDialog() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ full_name: "", phone: "", alt_phone: "", email: "", pan: "", aadhaar: "", city: "Siddipet", is_loan_customer: true, is_insurance_customer: false });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const mut = useMutation({
    mutationFn: async (v) => (await api.post("/customers", v)).data,
    onSuccess: () => { toast.success("Customer created"); qc.invalidateQueries({ queryKey: ["customers"] }); setOpen(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const submit = (e) => { e.preventDefault(); mut.mutate({ ...f, email: f.email || null, pan: f.pan || null, aadhaar: f.aadhaar || null, alt_phone: f.alt_phone || null }); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="bg-[#0052FF] hover:bg-[#003FCC] text-white text-sm font-semibold px-4 py-2 rounded-full inline-flex items-center gap-1.5" data-testid="new-customer-btn">
          <Plus size={14} /> New Customer
        </button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle>New Customer</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2" data-testid="new-customer-form">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Full name *</Label><Input required value={f.full_name} onChange={set("full_name")} data-testid="new-customer-name" /></div>
            <div><Label>Mobile *</Label><Input required value={f.phone} onChange={set("phone")} data-testid="new-customer-phone" /></div>
            <div><Label>Alt mobile</Label><Input value={f.alt_phone} onChange={set("alt_phone")} /></div>
            <div><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} /></div>
            <div><Label>PAN</Label><Input value={f.pan} onChange={set("pan")} /></div>
            <div><Label>Aadhaar</Label><Input value={f.aadhaar} onChange={set("aadhaar")} /></div>
            <div className="col-span-2"><Label>City</Label><Input value={f.city} onChange={set("city")} /></div>
          </div>
          <div className="flex gap-4 pt-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.is_loan_customer} onChange={(e)=>setF({...f, is_loan_customer: e.target.checked})} data-testid="new-customer-loan" /> Loan customer
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.is_insurance_customer} onChange={(e)=>setF({...f, is_insurance_customer: e.target.checked})} data-testid="new-customer-insurance" /> Insurance customer
            </label>
          </div>
          <button type="submit" disabled={mut.isPending} className="w-full bg-[#0052FF] hover:bg-[#003FCC] text-white font-semibold px-4 py-2.5 rounded-full disabled:opacity-60" data-testid="new-customer-submit">
            {mut.isPending ? "Saving…" : "Create Customer"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const { data: customers = [] } = useQuery({
    queryKey: ["customers", q, type],
    queryFn: async () => (await api.get("/customers", { params: { q: q || undefined, customer_type: type || undefined } })).data,
  });

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#06152D]">Customer 360</h1>
          <p className="text-sm text-[#52525B]">Master profile for every customer. Search by mobile / Customer ID / Loan File / Vehicle / Aadhaar / PAN.</p>
        </div>
        <NewCustomerDialog />
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
          <Input placeholder="Search mobile / PAN / Aadhaar / vehicle / file no." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customers-search" />
        </div>
        <Select value={type || "all"} onValueChange={(v) => setType(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="customers-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All customers</SelectItem>
            <SelectItem value="loan">Loan customers</SelectItem>
            <SelectItem value="insurance">Insurance customers</SelectItem>
            <SelectItem value="both">Both loan & insurance</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 bg-white border border-[#E4E4E7] rounded-xl overflow-hidden">
        <table className="w-full lj-table" data-testid="customers-table">
          <thead>
            <tr>
              <th>Customer ID</th><th>Name</th><th>Mobile</th><th>City</th>
              <th>Type</th><th>Income</th><th>Since</th><th></th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 && <tr><td colSpan="8" className="text-center text-[#52525B] py-8">No customers yet.</td></tr>}
            {customers.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                <td className="font-mono-num text-xs font-semibold text-[#06152D]">{c.customer_id}</td>
                <td className="font-medium text-[#09090b]">{c.full_name}</td>
                <td className="font-mono-num text-[#52525B]">
                  {c.phone}{c.alt_phone && <div className="text-xs text-[#94A3B8]">Alt: {c.alt_phone}</div>}
                </td>
                <td>{c.city || "—"}</td>
                <td>
                  <div className="flex gap-1">
                    {c.is_loan_customer && <Badge variant="secondary" className="bg-blue-100 text-blue-800">Loan</Badge>}
                    {c.is_insurance_customer && <Badge variant="secondary" className="bg-violet-100 text-violet-800">Insurance</Badge>}
                  </div>
                </td>
                <td className="font-mono-num">{c.monthly_income ? inr(c.monthly_income) : "—"}</td>
                <td className="text-xs text-[#52525B]">{new Date(c.created_at).toLocaleDateString("en-IN")}</td>
                <td>
                  <Link to={`/admin/customers/${c.id}`} className="text-xs text-[#0052FF] hover:text-[#003FCC] font-semibold inline-flex items-center gap-1" data-testid={`view-customer-${c.id}`}>
                    View <ExternalLink size={12} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
